import type {
  AppAccessRole,
  AccessUser,
  Device,
  LoginJournalEntry,
  LoginType,
  MenuCategory,
  MenuItem,
  Order,
  OrderStatus,
  PaymentStatus,
  Table,
  VenuePhoto,
  VenuePhotoType,
  WaiterCall,
} from './types';

// --- API request and response models ---
export interface MenuListResponse {
  categories: MenuCategory[];
  items: MenuItem[];
}

export interface MenuItemResponse {
  menuItem: MenuItem;
}

export interface TableResponse {
  table: Table;
}

export interface TableAccessInfo {
  id: string;
  name: string;
  accessCode: string;
  active: boolean;
  status?: 'active' | 'offline';
}

export interface TableAccessResponse {
  table: TableAccessInfo;
  accessFingerprint: string;
  // Only after the right table code: proof sent with guest orders.
  tableToken?: string;
}

export interface CreateMenuItemRequest {
  name: string;
  category: string;
  price: number;
  currency: string;
  description?: string;
  localImagePath?: string;
  available?: boolean;
}

export interface UpdateMenuItemRequest extends Partial<CreateMenuItemRequest> {}

export interface OrderPayload {
  tableId: string;
  items: Array<{
    menuItemId: string;
    quantity: number;
    modifiers?: string[];
  }>;
}

export interface CreateOrderRequest extends OrderPayload {
  staffUserId?: string | null;
}

export interface CreateOrderResponse {
  order: Order;
}

export interface OrderListResponse {
  orders: Order[];
}

export interface OrderDetailResponse {
  order: Order;
}

export interface UpdateOrderStatusRequest {
  status: OrderStatus;
}

export interface UpdateOrderStatusResponse {
  order: Order;
}

export interface UpdateOrderTimerRequest {
  // Countdown length in seconds; null or 0 clears the timer.
  durationSec: number | null;
}

export interface UpdateOrderTimerResponse {
  order: Order;
}

export interface UpdatePaymentStatusRequest {
  paymentStatus: PaymentStatus;
}

export interface UpdatePaymentStatusResponse {
  order: Order;
}

export interface CreateTableRequest {
  name: string;
  accessCode: string;
  pin: string;
  active: boolean;
  capacity?: number;
  note?: string;
}

export interface UpdateTableRequest extends Partial<CreateTableRequest> {}

export interface DeleteTableRequest {
  closeRelatedOrders?: boolean;
}

export interface VerifyTableAccessRequest {
  pin: string;
}

export interface TableListResponse {
  tables: Table[];
}

export interface WaiterCallResponse {
  waiterCall: WaiterCall;
}

export interface WaiterCallListResponse {
  waiterCalls: WaiterCall[];
}

export interface DeviceListResponse {
  devices: Device[];
}

export interface TableDetailResponse {
  table: Table;
}

export interface DeviceDetailResponse {
  device: Device;
}

export interface AdminOverviewResponse {
  totalOrders: number;
  totalRevenue: number;
  pendingPayments: number;
  statusCounts: Record<OrderStatus, number>;
  activeTables: number;
  activeDevices: number;
}

export interface AccessLoginRequest {
  username?: string;
  password?: string;
  token?: string;
  loginType: LoginType;
  deviceName?: string;
  note?: string;
}

export interface AccessLoginResponse {
  user: AccessUser;
  loginEntry: LoginJournalEntry;
}

export interface AccessUserListResponse {
  users: AccessUser[];
}

export interface AccessUserResponse {
  user: AccessUser;
}

export interface AdminQrCard {
  id: string;
  fullName: string;
  loginUrl: string;
}

export interface AdminQrCardsResponse {
  cards: AdminQrCard[];
}

export interface CreateAccessUserRequest {
  fullName: string;
  username: string;
  password: string;
  role: AppAccessRole;
  email?: string;
  note?: string;
}

export interface UpdateAccessUserRequest {
  fullName?: string;
  username?: string;
  email?: string;
  password?: string;
  recoveryCode?: string;
  role?: AppAccessRole;
  note?: string;
}

export interface DeleteAccessUserRequest {
  currentUserId: string;
}

export interface AccessSetupResponse {
  accessSetupComplete: boolean;
}

export interface AccessStatusResponse {
  hasAdminUser: boolean;
  hasAnyNormalAdmin: boolean;
  accessSetupComplete: boolean;
}

export interface UpdateAccessSetupRequest {
  accessSetupComplete: boolean;
}

export type StorefrontTransitionMode = 'none' | 'fade' | 'slide' | 'zoom' | 'random';

export interface AppSettingsResponse {
  venueName: string;
  venueDescription: string;
  venueOpeningHours: string;
  venuePhone: string;
  venueAddress: string;
  venuePostalCode: string;
  venueHoursMonday: string;
  venueHoursTuesday: string;
  venueHoursWednesday: string;
  venueHoursThursday: string;
  venueHoursFriday: string;
  venueHoursSaturday: string;
  venueHoursSunday: string;
  storefrontShowPrices: boolean;
  appSettingsComplete: boolean;
  venueCurrency: string;
  venueEmail: string;
  storefrontDishesPerScreen: number;
  storefrontTransitionMode: StorefrontTransitionMode;
  kitchenUrl: string;
  staffUrl: string;
  lanBaseUrl: string;
}

export interface UpdateAppSettingsRequest {
  venueName?: string;
  venueDescription?: string;
  venueOpeningHours?: string;
  venuePhone?: string;
  venueAddress?: string;
  venuePostalCode?: string;
  venueHoursMonday?: string;
  venueHoursTuesday?: string;
  venueHoursWednesday?: string;
  venueHoursThursday?: string;
  venueHoursFriday?: string;
  venueHoursSaturday?: string;
  venueHoursSunday?: string;
  storefrontShowPrices?: boolean;
  appSettingsComplete?: boolean;
  venueCurrency?: string;
  venueEmail?: string;
  storefrontDishesPerScreen?: number;
  storefrontTransitionMode?: StorefrontTransitionMode;
}

export interface InitialAccessSetupRequest {
  fullName: string;
  username: string;
  password: string;
  recoveryCode: string;
  deviceName?: string;
}

export interface RecoverPasswordRequest {
  username: string;
  recoveryCode: string;
  newPassword: string;
}

export interface RecoverPasswordResponse {
  success: boolean;
  message: string;
}

export interface LoginJournalResponse {
  logins: LoginJournalEntry[];
}

export interface ClearLoginJournalResponse {
  success: boolean;
}

export interface LocalNetworkInfoResponse {
  lanBaseUrl: string;
  host: string;
  port: number;
  availableHosts: string[];
}

export type BackupWindowKey = 'hour' | 'day' | 'week' | 'month';

export interface DatabaseBackupEntry {
  id: string;
  fileName: string;
  createdAt: string;
  sizeBytes: number;
}

export interface DatabaseBackupCatalogResponse {
  autoBackupEnabled: boolean;
  latest: DatabaseBackupEntry | null;
  windows: Record<BackupWindowKey, DatabaseBackupEntry | null>;
  backups: DatabaseBackupEntry[];
}

export interface CreateDatabaseBackupResponse {
  success: boolean;
  backup: DatabaseBackupEntry;
  catalog: DatabaseBackupCatalogResponse;
}

export interface RestoreDatabaseBackupRequest {
  backupId: string;
  password: string;
}

export interface RestoreDatabaseBackupResponse {
  success: boolean;
  restoredBackup: DatabaseBackupEntry;
  catalog: DatabaseBackupCatalogResponse;
}

export interface UpdateDatabaseBackupSettingsRequest {
  enabled: boolean;
}

export interface UpdateDatabaseBackupSettingsResponse {
  success: boolean;
  enabled: boolean;
  catalog: DatabaseBackupCatalogResponse;
}

export type BillingCycle = 'monthly' | 'yearly';
export type BillingProvider = 'revolut' | 'stripe';
export type BillingCheckoutStatus = 'pending' | 'requires_action' | 'active' | 'completed' | 'failed' | 'cancelled';
export type BillingEntitlementStatus = 'pending' | 'active' | 'cancelled' | 'overdue' | 'expired';

// --- Единая подписка «Cafe Local Pro» (Stripe) ---
export type SubscriptionStatus =
  | 'free'
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'unpaid'
  | 'incomplete'
  | 'canceled';

export interface Subscription {
  id: string;
  plan: string;
  status: SubscriptionStatus;
  billingCycle: BillingCycle | '';
  provider: BillingProvider;
  currency: string;
  amountCents: number | null;
  customerEmail: string;
  customerName: string;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  stripeCheckoutSessionId: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  activatedAt: string;
  canceledAt: string;
  updatedAt: string;
}

export interface SubscriptionStatusResponse {
  subscription: Subscription;
  plan: { id: string; name: string };
  isPro: boolean;
  limits: { tables: number; menuItems: number };
  usage: { tables: number; menuItems: number };
  stripeConfigured: boolean;
}

export interface CreateStripeCheckoutRequest {
  returnBaseUrl?: string;
  billingCycle: BillingCycle;
  customerEmail: string;
  customerName?: string;
  currency?: 'uah' | 'eur';
  // Язык интерфейса админа: пробрасывается в Stripe Checkout (locale) и в URL возврата.
  lang?: string;
  // Куда вернуть после оплаты: '/admin/subscription' (по умолчанию) или '/v2/ai'.
  returnPath?: string;
}

export interface CreateStripeCheckoutResponse {
  checkout: BillingCheckout;
  checkoutUrl: string;
}

export interface StripePortalResponse {
  url: string;
}

// Структура ответа сервера при достижении лимита бесплатного тарифа (HTTP 402).
export interface SubscriptionLimitError {
  error: 'subscription_required';
  limitReached: true;
  kind: 'tables' | 'menuItems';
  limit: number;
  current: number;
  plan: { id: string; name: string };
  message: string;
}

export interface BillingEntitlement {
  featureId: string;
  featureName: string;
  status: BillingEntitlementStatus;
  provider: BillingProvider;
  billingCycle: BillingCycle;
  customerEmail: string;
  customerName: string;
  checkoutId: string;
  providerSubscriptionId: string;
  providerSetupOrderId: string;
  activatedAt: string;
  updatedAt: string;
}

export interface BillingCheckout {
  id: string;
  featureId: string;
  featureName: string;
  provider: BillingProvider;
  billingCycle: BillingCycle;
  amountCents: number;
  currency: string;
  customerEmail: string;
  customerName: string;
  status: BillingCheckoutStatus;
  providerCheckoutUrl: string;
  providerSubscriptionId: string;
  providerSetupOrderId: string;
  providerOrderState: string;
  externalReference: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface BillingCheckoutsResponse {
  checkouts: BillingCheckout[];
}

// Используется страницей администратора портала (helfer.world).
export interface PortalAdminBillingResponse {
  checkouts: BillingCheckout[];
  entitlements: BillingEntitlement[];
}

export interface SupportRequestPayload {
  fullName: string;
  contact: string;
  message: string;
  source: 'portal' | 'admin';
}

export interface SupportRequestResponse {
  success: boolean;
}

export interface PortalVenueProfile {
  id: string;
  name: string;
  email?: string;
  description: string;
  phone: string;
  address: string;
  postalCode: string;
  openingHours: string;
  hoursMonday: string;
  hoursTuesday: string;
  hoursWednesday: string;
  hoursThursday: string;
  hoursFriday: string;
  hoursSaturday: string;
  hoursSunday: string;
  entranceImagePath: string;
  previewImagePath: string;
  assetBaseUrl?: string;
  latitude: number | null;
  longitude: number | null;
  presenceStatus: 'open' | 'closed';
  enabled?: boolean;
  lastSeenAt: string;
  updatedAt: string;
  menuCategories?: Array<{ id: string; name: string }> | null;
  menuItems?: Array<{ id: string; name: string; category: string; price: number; currency: string; description?: string; available: boolean; imageUrl?: string }> | null;
  occupancy?: { activeTables: number; activeOrderedTables: number; freeTables?: number; percent: number } | null;
  averageRating?: number | null;
  reviewCount?: number;
}

export interface PortalReview {
  id: string;
  venueId: string;
  author: string;
  rating: number;
  comment: string;
  createdAt: string;
}

export interface PortalMenuItem {
  id: string;
  category: string;
  name: string;
  price: number;
  currency: string;
  description?: string;
  available: boolean;
  localImagePath?: string;
}

export interface PortalMenuCategory {
  id: string;
  name: string;
}

export interface PortalOccupancy {
  totalActiveTables: number;
  occupiedTables: number;
  percent: number;
  updatedAt?: string;
}

export interface PortalVenueProfileResponse {
  profile: PortalVenueProfile | null;
  reviews: PortalReview[];
  menuCategories: PortalMenuCategory[];
  menuItems: PortalMenuItem[];
  occupancy: PortalOccupancy | null;
}

export interface PortalVenueMapEntry {
  id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  occupancy: {
    totalActiveTables: number;
    occupiedTables: number;
    percent: number;
  };
}

export interface PortalVenuesResponse {
  venues: PortalVenueMapEntry[];
}

export interface PortalVenueListResponse {
  profiles: PortalVenueProfile[];
}

export interface PortalAdminAuthRequest {
  ownerEmail: string;
}

export interface PortalAdminAuthResponse {
  ok: boolean;
  email: string;
}

export interface UpdatePortalVenueEnabledRequest {
  enabled: boolean;
}

export interface UpdatePortalVenueEnabledResponse {
  profile: PortalVenueProfile;
}

export interface UpdatePortalVenueLocationRequest {
  latitude: number;
  longitude: number;
}

export interface UpdatePortalVenueLocationResponse {
  profile: PortalVenueProfile;
  sync?: { ok?: boolean; skipped?: boolean; reason?: string; status?: number; error?: string };
}

export interface CreatePortalReviewRequest {
  venueId: string;
  author?: string;
  rating: number;
  comment: string;
}

export interface CreatePortalReviewResponse {
  review: PortalReview;
}

export type PortalReportTargetType = 'venue' | 'review';
export type PortalReportStatus = 'new' | 'reviewing' | 'resolved' | 'dismissed';
export type PortalReportReason =
  | 'wrong_location'
  | 'fake_venue'
  | 'offensive_content'
  | 'wrong_menu'
  | 'wrong_prices'
  | 'privacy'
  | 'spam'
  | 'other';

export interface PortalReport {
  id: string;
  venueId: string;
  venueName?: string;
  venueAddress?: string;
  reviewId?: string;
  reviewAuthor?: string;
  reviewComment?: string;
  targetType: PortalReportTargetType;
  reason: PortalReportReason;
  comment: string;
  contact: string;
  status: PortalReportStatus;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
}

export interface CreatePortalReportRequest {
  venueId: string;
  reviewId?: string;
  targetType: PortalReportTargetType;
  reason: PortalReportReason;
  comment?: string;
  contact?: string;
}

export interface CreatePortalReportResponse {
  report: PortalReport;
}

export interface PortalReportsResponse {
  reports: PortalReport[];
}

export interface UpdatePortalReportRequest {
  status: PortalReportStatus;
  action?: 'hideVenue' | 'deleteReview' | 'none';
}

export interface UpdatePortalReportResponse {
  report: PortalReport;
  actionResult?: { ok: boolean; action: string };
}

export interface SalesDelta {
  percent: number;
  direction: 'up' | 'down' | 'flat';
  absolute: number;
}

export interface SalesStatisticsSummary {
  currency: string;
  revenue: number;
  ordersCount: number;
  averageCheck: number;
  dishesSold: number;
  activeTables: number;
  revenueDelta: SalesDelta;
  ordersDelta: SalesDelta;
  averageCheckDelta: SalesDelta;
  dishesSoldDelta: SalesDelta;
  activeTablesDelta: SalesDelta;
}

export interface SalesStatisticsDish {
  dishId: string;
  name: string;
  price: number;
  currency: string;
  quantity: number;
  revenue: number;
  categoryId: string;
  categoryName: string;
  previousRevenue: number;
  previousQuantity: number;
  deltaRevenue: SalesDelta;
  deltaQuantity: SalesDelta;
}

export interface SalesStatisticsCategory {
  categoryId: string;
  name: string;
  quantity: number;
  revenue: number;
  currency: string;
  sharePercent: number;
  deltaRevenue: SalesDelta;
}

export interface SalesStatisticsChartPoint {
  date: string;
  revenue: number;
}

export interface SalesStatisticsResponse {
  selectedDate: string;
  comparisonDate: string;
  summary: SalesStatisticsSummary;
  topDishes: SalesStatisticsDish[];
  categories: SalesStatisticsCategory[];
  chart: {
    currentPeriod: SalesStatisticsChartPoint[];
    previousPeriod: SalesStatisticsChartPoint[];
  };
}

export interface VenuePhotoListResponse {
  photos: VenuePhoto[];
}

export interface CreateVenuePhotoRequest {
  type: VenuePhotoType;
  imagePath: string;
  previewImagePath?: string;
  sortOrder?: number;
  replace?: boolean;
}

export interface VenuePhotoResponse {
  photo: VenuePhoto;
}

// --- Endpoint map ---
export const ApiEndpoints = {
  menu: {
    list: { method: 'GET', path: '/api/menu' },
    createItem: { method: 'POST', path: '/api/menu/items' },
    updateItem: (itemId: string) => ({ method: 'PUT' as const, path: `/api/menu/items/${itemId}` }),
    deleteItem: (itemId: string) => ({ method: 'DELETE' as const, path: `/api/menu/items/${itemId}` }),
    categories: { method: 'GET', path: '/api/menu/categories' },
    item: (itemId: string) => ({ method: 'GET' as const, path: `/api/menu/${itemId}` }),
  },
  orders: {
    list: { method: 'GET', path: '/api/orders' },
    detail: (orderId: string) => ({ method: 'GET' as const, path: `/api/orders/${orderId}` }),
    create: { method: 'POST', path: '/api/orders' },
    updateStatus: (orderId: string) => ({ method: 'PATCH' as const, path: `/api/orders/${orderId}/status` }),
    updatePayment: (orderId: string) => ({ method: 'PATCH' as const, path: `/api/orders/${orderId}/payment` }),
    updateTimer: (orderId: string) => ({ method: 'PATCH' as const, path: `/api/orders/${orderId}/timer` }),
  },
  tables: {
    list: { method: 'GET', path: '/api/tables' },
    create: { method: 'POST', path: '/api/tables' },
    detail: (tableId: string) => ({ method: 'GET' as const, path: `/api/tables/${tableId}` }),
    update: (tableId: string) => ({ method: 'PUT' as const, path: `/api/tables/${tableId}` }),
    delete: (tableId: string) => ({ method: 'DELETE' as const, path: `/api/tables/${tableId}` }),
    waiterCalls: { method: 'GET', path: '/api/tables/waiter-calls' },
    access: (accessCode: string) => ({ method: 'GET' as const, path: `/api/tables/access/${accessCode}` }),
    verifyAccess: (accessCode: string) => ({ method: 'POST' as const, path: `/api/tables/access/${accessCode}/verify` }),
    callWaiter: (tableId: string) => ({ method: 'POST' as const, path: `/api/tables/${tableId}/call-waiter` }),
    resolveWaiterCall: (tableId: string) => ({ method: 'PATCH' as const, path: `/api/tables/${tableId}/call-waiter` }),
  },
  devices: {
    list: { method: 'GET', path: '/api/devices' },
    detail: (deviceId: string) => ({ method: 'GET' as const, path: `/api/devices/${deviceId}` }),
  },
  admin: {
    overview: { method: 'GET', path: '/api/admin/overview' },
    statistics: { method: 'GET', path: '/api/admin/statistics' },
    loginJournal: { method: 'GET', path: '/api/admin/login-journal' },
    clearLoginJournal: { method: 'DELETE', path: '/api/admin/login-journal' },
    localNetwork: { method: 'GET', path: '/api/admin/local-network' },
    backups: { method: 'GET', path: '/api/admin/backups' },
    createBackup: { method: 'POST', path: '/api/admin/backups' },
    restoreBackup: { method: 'POST', path: '/api/admin/backups/restore' },
    updateBackupSettings: { method: 'PATCH', path: '/api/admin/backups/settings' },
  },
  support: {
    submit: { method: 'POST', path: '/api/support/contact' },
  },
  billing: {
    subscription: { method: 'GET', path: '/api/billing/subscription' },
    checkouts: { method: 'GET', path: '/api/billing/checkouts' },
    createStripeCheckout: { method: 'POST', path: '/api/billing/stripe/checkout' },
    stripePortal: { method: 'POST', path: '/api/billing/stripe/portal' },
  },
  portal: {
    venues: { method: 'GET', path: '/api/portal/venues' },
    venueProfile: { method: 'GET', path: '/api/portal/venue-profile' },
    updateVenueLocation: { method: 'PATCH', path: '/api/portal/venue-profile/location' },
    createReview: { method: 'POST', path: '/api/portal/reviews' },
    createReport: { method: 'POST', path: '/api/portal/reports' },
    venueReviews: (venueId: string) => ({ method: 'GET' as const, path: `/api/portal/venues/${venueId}/reviews` }),
    adminAuth: { method: 'POST', path: '/api/portal/admin/auth' },
    adminVenues: { method: 'GET', path: '/api/portal/admin/venues' },
    adminBilling: { method: 'GET', path: '/api/portal/admin/billing' },
    adminReports: { method: 'GET', path: '/api/portal/admin/reports' },
    updateAdminVenue: (venueId: string) => ({ method: 'PATCH' as const, path: `/api/portal/admin/venues/${venueId}` }),
    updateAdminReport: (reportId: string) => ({ method: 'PATCH' as const, path: `/api/portal/admin/reports/${reportId}` }),
  },
  venue: {
    photos: { method: 'GET', path: '/api/venue/photos' },
    createPhoto: { method: 'POST', path: '/api/venue/photos' },
    deletePhoto: (photoId: string) => ({ method: 'DELETE' as const, path: `/api/venue/photos/${photoId}` }),
  },
  access: {
    login: { method: 'POST', path: '/api/access/login' },
    loginWithQr: { method: 'POST', path: '/api/access/qr' },
    status: { method: 'GET', path: '/api/access/status' },
    initialSetup: { method: 'POST', path: '/api/access/initial-setup' },
    recoverPassword: { method: 'POST', path: '/api/access/recover-password' },
    users: { method: 'GET', path: '/api/access/users' },
    createUser: { method: 'POST', path: '/api/access/users' },
    updateUser: (userId: string) => ({ method: 'PATCH' as const, path: `/api/access/users/${userId}` }),
    deleteUser: (userId: string) => ({ method: 'DELETE' as const, path: `/api/access/users/${userId}` }),
    getSetup: { method: 'GET', path: '/api/access/setup' },
    updateSetup: { method: 'PATCH', path: '/api/access/setup' },
    adminQrCards: { method: 'GET', path: '/api/access/admin-qr-cards' },
  },
  appSettings: {
    get: { method: 'GET', path: '/api/app/settings' },
    update: { method: 'PATCH', path: '/api/app/settings' },
  },
  costing: {
    suppliers: { method: 'GET', path: '/api/costing/suppliers' },
    createSupplier: { method: 'POST', path: '/api/costing/suppliers' },
    updateSupplier: (supplierId: string) => ({ method: 'PATCH' as const, path: `/api/costing/suppliers/${supplierId}` }),
    deleteSupplier: (supplierId: string) => ({ method: 'DELETE' as const, path: `/api/costing/suppliers/${supplierId}` }),
    ingredients: { method: 'GET', path: '/api/costing/ingredients' },
    ingredient: (ingredientId: string) => ({ method: 'GET' as const, path: `/api/costing/ingredients/${ingredientId}` }),
    createIngredient: { method: 'POST', path: '/api/costing/ingredients' },
    updateIngredient: (ingredientId: string) => ({ method: 'PATCH' as const, path: `/api/costing/ingredients/${ingredientId}` }),
    deleteIngredient: (ingredientId: string) => ({ method: 'DELETE' as const, path: `/api/costing/ingredients/${ingredientId}` }),
    ingredientPriceHistory: (ingredientId: string) => ({ method: 'GET' as const, path: `/api/costing/ingredients/${ingredientId}/price-history` }),
    dishRecipe: (menuItemId: string) => ({ method: 'GET' as const, path: `/api/costing/dishes/${menuItemId}/recipe` }),
    updateDishRecipe: (menuItemId: string) => ({ method: 'PUT' as const, path: `/api/costing/dishes/${menuItemId}/recipe` }),
    dishCosting: (menuItemId: string) => ({ method: 'GET' as const, path: `/api/costing/dishes/${menuItemId}/costing` }),
    setManualDishCost: (menuItemId: string) => ({ method: 'POST' as const, path: `/api/costing/dishes/${menuItemId}/manual-cost` }),
    setDishCostMode: (menuItemId: string) => ({ method: 'POST' as const, path: `/api/costing/dishes/${menuItemId}/cost-mode` }),
    dishCostHistory: (menuItemId: string) => ({ method: 'GET' as const, path: `/api/costing/dishes/${menuItemId}/cost-history` }),
    analytics: (query: string) => ({ method: 'GET' as const, path: `/api/costing/analytics${query ? `?${query}` : ''}` }),
  },
} as const;

// --- Event names for future realtime sync ---
export const ApiEventNames = {
  orderCreated: 'order.created',
  orderUpdated: 'order.updated',
  orderStatusChanged: 'order.statusChanged',
  orderPaymentUpdated: 'order.paymentUpdated',
  tableStatusChanged: 'table.statusChanged',
  deviceStatusChanged: 'device.statusChanged',
  adminOverviewUpdated: 'admin.overviewUpdated',
} as const;

// --- Minimal future backend integration structure ---
// Suggested file structure for the next stage:
// src/shared/api-contract.ts          (this contract definition)
// src/api/requests.ts                 (typed client requests)
// src/api/responses.ts                (typed backend responses)
// src/api/endpoints.ts                (path helpers and route builders)
// src/store/api.ts                    (store adapters for backend integration)
// src/services/backend.ts             (future fetch/axios adapter)
